import type { SceneNode } from "@uzverse/world-scene";
import { footprintOf } from "./generators.js";

export type Vec3 = readonly [number, number, number];

export interface SceneBounds {
  center: Vec3;
  /** Radius of a sphere around `center` that contains every building. */
  radius: number;
}

const generatorOf = (n: SceneNode): { generator: string; params: Record<string, unknown> } =>
  n.geometry.kind === "procedural"
    ? { generator: n.geometry.generator, params: n.geometry.params }
    : { generator: "asset", params: {} };

/** A sphere around the buildings and objects, used to frame the camera the first time. */
export function sceneBounds(nodes: Iterable<SceneNode>): SceneBounds {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let maxY = 0;
  let any = false;
  for (const n of nodes) {
    if (n.parentId !== null || (n.kind !== "building" && n.kind !== "object")) continue;
    const { generator, params } = generatorOf(n);
    const f = footprintOf(generator, params);
    const [x, , z] = n.transform.position;
    minX = Math.min(minX, x - f.width / 2);
    maxX = Math.max(maxX, x + f.width / 2);
    minZ = Math.min(minZ, z - f.depth / 2);
    maxZ = Math.max(maxZ, z + f.depth / 2);
    maxY = Math.max(maxY, f.height);
    any = true;
  }
  if (!any) return { center: [0, 0, 0], radius: 40 };
  return {
    center: [(minX + maxX) / 2, maxY / 4, (minZ + maxZ) / 2],
    radius: Math.max(40, Math.hypot(maxX - minX, maxZ - minZ, maxY) / 2),
  };
}

/** True for the seeded landmark: a world-owned object drawn as a `building.box`. */
export function isLandmark(n: SceneNode): boolean {
  return (
    n.entityType === "world.object" &&
    n.ownerCompanyId === null &&
    n.geometry.kind === "procedural" &&
    n.geometry.generator === "building.box"
  );
}

/** Buildings the side panel lists: company placements and the landmark. */
export function isBuildingNode(n: SceneNode): boolean {
  return (
    n.parentId === null &&
    n.pickable &&
    (n.kind === "building" || (n.kind === "object" && isLandmark(n)))
  );
}
